// External libraries
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';

// Custom components
import { RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import InputLocation from '../../../../../react-components/InputLocation';
import Text from '../../../../../react-components/Text';

// Domain
import { Location } from '@nx-smartmonkey/shared/domain';

// Helpers
import { TimeWindows } from '@nx-smartmonkey/shared/interfaces';
import { intl } from '../../../../../helpers/IntGlobalProvider';

interface AddressReviewItemProps {
  serviceId: string;
  serviceLabel?: string;
  location?: Location & { original_input?: string };
  onAddressChange: (location?: Location | null) => void;
}

const AddressReviewItem = ({ serviceId, serviceLabel, location, onAddressChange }: AddressReviewItemProps) => (
  <Grid container direction="row" alignItems="center" wrap="nowrap">
    <Grid item container>
      {serviceLabel ? (
        <Text variant="body" style={{ fontSize: `12px` }}>
          {serviceLabel}
        </Text>
      ) : (
        <Text variant="body" style={{ color: RoutalPalette.secondary00, fontSize: `12px` }} intlId="no_name" />
      )}
    </Grid>

    <Grid item container>
      {location?.original_input ? (
        <Text variant="body" style={{ fontSize: `12px` }}>
          {location?.original_input}
        </Text>
      ) : (
        <Text variant="body" style={{ color: RoutalPalette.secondary00, fontSize: `12px` }} intlId="none" />
      )}
    </Grid>

    <Grid item container>
      <InputLocation
        id={serviceId}
        fullWidth
        inputProps={{
          styles: {
            fontSize: `12px`,
          },
          size: `medium`,
          variant: `outlined`,
          placeholderIntlId: `location`,
        }}
        emptyIntlId="no_results"
        fieldValue={location}
        onUpdate={onAddressChange}
      />
    </Grid>

    <Grid item>
      <Grid container style={{ width: `50px` }} justifyContent="flex-end">
        {!location?.label ? (
          <RoutalTooltip title={intl.formatMessage({ id: `plans.dialogs.services_from_file.review.error` })}>
            <ErrorOutlineOutlinedIcon style={{ marginRight: `16px`, color: RoutalPalette.danger }} />
          </RoutalTooltip>
        ) : (
          <>
            {location.partial_match || !location.isValid() ? (
              <RoutalTooltip title={intl.formatMessage({ id: `plans.dialogs.services_from_file.review.alert` })}>
                <ReportProblemOutlinedIcon style={{ marginRight: `16px`, color: RoutalPalette.warning }} />
              </RoutalTooltip>
            ) : null}
          </>
        )}
      </Grid>
    </Grid>
  </Grid>
);

type ReviewStopDataTypes = `delivery` | `pickup`;

interface StopData {
  duration?: number;
  requires?: string[];
  time_windows?: TimeWindows;
  volume?: number;
  weight?: number;
  client_id?: string;
  client_external_id?: string;
  reference_person?: string;
  location?: Location;
  location_details?: string;
  comments?: string;
  phone?: string;
  email?: string;
  url?: string;
  custom_fields?: object;
}

interface ReviewStopData extends StopData {
  label?: string;
  pickup?: StopData & { label?: string };
  originalIndex?: number;
  type?: ReviewStopDataTypes;
}

interface ImportStopsDialogReviewProps {
  stops: ReviewStopData[];
  onAddressChange: ({
    stopIndex,
    newAddress,
    isPickup,
  }: {
    stopIndex: number;
    newAddress?: Location;
    isPickup?: boolean;
  }) => void;
}

export const ImportStopsDialogReview = ({ stops, onAddressChange }: ImportStopsDialogReviewProps) => {
  const [realStops, setRealStops] = useState<ReviewStopData[]>([]);
  const [qualitySort, setQualitySort] = useState<boolean>(false);

  useEffect(() => {
    const deliveries = stops.map((stop: ReviewStopData, idx: number) => {
      const { pickup, ...delivery } = stop;
      return { ...delivery, originalIndex: idx, type: `delivery` as ReviewStopDataTypes };
    });
    const pickups = stops
      .filter((stop: ReviewStopData) => !!stop.pickup)
      .map((stop: ReviewStopData, idx: number) => {
        const { pickup } = stop;
        return { ...pickup, originalIndex: idx, type: `pickup` as ReviewStopDataTypes };
      });
    setRealStops([...deliveries, ...pickups]);
  }, [stops]);

  const hasAlerts = () =>
    realStops.some((stop: ReviewStopData) => stop.location?.partial_match || !stop.location?.isValid());

  const sortStops = () => {
    const sortOrder = qualitySort ? 1 : -1;

    return realStops.sort((a: ReviewStopData, b: ReviewStopData) => {
      if (!a.location?.lat && !a.location?.lng) return sortOrder;

      if (!b.location?.lat && !b.location?.lng) return -sortOrder;

      if (!a.location.isValid()) return sortOrder;

      if (!b.location.isValid()) return -sortOrder;

      return ((a.location?.partial_match ?? false) > (b.location?.partial_match ?? false) ? 1 : -1) * sortOrder;
    });
  };

  return (
    <Grid container direction="column">
      {hasAlerts() ? (
        <>
          <Grid item container justifyContent="center">
            <Text
              variant="body"
              style={{ fontSize: `12px`, color: RoutalPalette.danger }}
              intlId="plans.dialogs.services_from_file.review.has_alerts"
            />
          </Grid>
          <VSpacer medium />
        </>
      ) : null}
      <Grid container direction="row" wrap="nowrap">
        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.label" />
        </Grid>

        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.input_location" />
        </Grid>

        <Grid item container>
          <Text variant="label" intlId="plans.dialogs.services_from_file.review.detected_location" />
        </Grid>

        <Grid item container style={{ width: `50px` }} wrap="nowrap">
          <Text
            variant="label"
            intlId="plans.dialogs.services_from_file.review.alerts"
            style={{ cursor: `pointer` }}
            onClick={() => setQualitySort(!qualitySort)}
          />
          <Grid container direction="column" style={{ marginTop: `-10px` }}>
            <ArrowDropUpIcon
              style={{
                cursor: `pointer`,
                color: qualitySort ? RoutalPalette.neutral60 : RoutalPalette.neutral20,
                height: `20px`,
              }}
              onClick={() => setQualitySort(true)}
            />
            <ArrowDropDownIcon
              style={{
                cursor: `pointer`,
                color: !qualitySort ? RoutalPalette.neutral60 : RoutalPalette.neutral20,
                height: `20px`,
              }}
              onClick={() => setQualitySort(false)}
            />
          </Grid>
        </Grid>
      </Grid>

      <VSpacer medium />

      {sortStops().map((stop: ReviewStopData, idx: number) => {
        return (
          <Grid container direction="column" key={`service-review-${stop.originalIndex}-${idx}`}>
            <AddressReviewItem
              serviceId={`${stop.type}-${stop.originalIndex}`}
              serviceLabel={stop.label}
              location={stop.location}
              onAddressChange={(location?: Location | null) =>
                onAddressChange({
                  stopIndex: stop.originalIndex!,
                  newAddress: location ?? undefined,
                  isPickup: stop.type === `pickup`,
                })
              }
            />
            <VSpacer small />
          </Grid>
        );
      })}
    </Grid>
  );
};
